import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { cabinsApi } from "../../api/cabins";
import { enrollmentsApi } from "../../api/enrollments";
import { filterCamperCabins } from "../../utils/cabinFilter";
import { formatCabinNumber, parseCabinNumber } from "../../utils/terminology";
import type { Enrollment, Cabin } from "../../types";

interface Props {
  enrollment: Enrollment | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Override-only dialog for an existing enrollment. The CampMinder-supplied
 * year/term/cabin stay underneath; only the override fields are touched.
 *
 * Persists via PATCH /enrollments/:id/override-cabin which:
 *   - Sets `override_cabin_id` to the picked cabin (or NULL to clear).
 *   - Records `override_reason`, `override_set_by_user_id`, `override_set_at`
 *     so we can answer "who moved this camper, when, and why?".
 *
 * Admin/director only — counselor/readonly never reach this dialog because
 * CamperDetail gates the affordance behind `isAdminOrDirector`, and the
 * backend re-checks via requireRole("admin","director").
 */
export default function OverrideCabinDialog({ enrollment, open, onClose, onSuccess }: Props) {
  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [loadingCabins, setLoadingCabins] = useState(false);
  const [overrideId, setOverrideId] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOverrideId(enrollment?.overrideCabinId ? String(enrollment.overrideCabinId) : "");
    setReason(enrollment?.overrideReason ?? "");
    setLoadingCabins(true);
    cabinsApi
      .list({ includeArchived: false })
      .then(setCabins)
      .catch(() => toast.error("Failed to load cabins"))
      .finally(() => setLoadingCabins(false));
  }, [open, enrollment]);

  if (!enrollment) return null;

  const baseCabinName = enrollment.cabin?.name ?? "—";
  const termLabel = enrollment.term?.name ?? `${enrollment.year}`;
  const hasOverride = !!enrollment.overrideCabinId;

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const cabinId = clear ? null : overrideId ? Number(overrideId) : null;
      await enrollmentsApi.overrideCabin(enrollment.id, {
        cabinId,
        reason: clear ? null : reason.trim() || null,
      });
      toast.success(
        clear ? "Override cleared" : cabinId != null ? "Override saved" : "No override set",
      );
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save override");
    } finally {
      setSaving(false);
    }
  };

  // Don't offer the base cabin as an override target — that's a no-op.
  // Drop LODGE/KITCHEN/non-numeric cabins so admins can't accidentally
  // move a camper into staff housing.
  const overrideOptions = filterCamperCabins(cabins.filter((c) => c.id !== enrollment.cabinId))
    .map((c) => {
      const numeric = parseCabinNumber(c.name);
      const label = numeric != null ? formatCabinNumber(c.name) : c.name;
      return { value: c.id, label };
    });

  return (
    <Modal open={open} onClose={onClose} title="Move camper" size="md">
      <div className="space-y-4">
        <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm">
          <div className="text-stone-500 text-xs uppercase tracking-wide">Underlying enrollment</div>
          <div className="mt-0.5 text-stone-800">
            {enrollment.year} · {termLabel} · {baseCabinName}
          </div>
          <p className="mt-1 text-xs text-stone-500">
            This record stays as-is — the override layers on top so a CampMinder re-sync will not blow it away.
          </p>
        </div>

        <Select
          label="Move to cabin"
          options={overrideOptions}
          placeholder={loadingCabins ? "Loading cabins…" : "No override (use cabin above)"}
          value={overrideId}
          onChange={(e) => setOverrideId(e.target.value)}
        />

        <div>
          <label className="block text-xs uppercase tracking-wide text-stone-500 mb-1">
            Reason <span className="text-stone-400 normal-case">(optional)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. moved up after parent request"
            className="block w-full rounded-md border border-stone-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
          />
          {enrollment.overrideSetAt && (
            <p className="mt-1 text-xs text-stone-500">
              Last changed{" "}
              {new Date(enrollment.overrideSetAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
              .
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <div>
            {hasOverride && (
              <Button type="button" variant="ghost" onClick={() => save(true)} loading={saving}>
                Clear override
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="button" variant="primary" onClick={() => save(false)} loading={saving}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
